import { FC, useState } from 'react';
import {
  Box,
  Button,
  HStack,
  Heading,
  Image,
  Table,
  TableContainer,
  Tbody,
  Td,
  Thead,
  Tr,
  InputGroup,
  InputLeftElement,
  Input,
  useDisclosure,
  IconButton,
  LinkBox,
  LinkOverlay,
  VStack,
} from '@chakra-ui/react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';

import PlusIcon from '../../../assets/icons/plus.svg';
import { ReactComponent as FiltersIcon } from '../../../assets/icons/filters.svg';
import { ReactComponent as SearchIcon } from '../../../assets/icons/search.svg';
import { ReactComponent as RedTrashIcon } from '../../../assets/icons/red-trash.svg';
import { ReactComponent as EditIcon } from '../../../assets/icons/edit.svg';
import {
  useDeletePhaseMutation,
  useGetAllPhasesQuery,
} from '../../../services';
import {
  FilterDrawer,
  CenteredSpinner,
  RoundedBadge,
  Paginator,
  TableHeader,
} from '../../../components';
import { formatDate } from '../../../utils';
import { CompanyStatus } from '../../../models';

import { DeletePhaseModal } from '.';
import { phasesFilters, phasesSorts } from '../utils';

export const PhasesList: FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>();
  const [page, setPage] = useState(0);
  const [selectedPhase, setSelectedProject] = useState<number | undefined>(
    undefined
  );
  const [filters, setFilters] = useState<{ status?: CompanyStatus }>({
    status: undefined,
  });

  const { projectId } = useParams();
  const navigate = useNavigate();

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const {
    isOpen: isDeletePhaseModalOpen,
    onOpen: onOpenDeletePhaseModal,
    onClose: onCloseDeletePhaseModal,
  } = useDisclosure();

  const { data, isLoading, refetch } = useGetAllPhasesQuery({
    page,
    phaseName: debouncedSearchTerm,
    projectId: Number(projectId),
    // ...filters,
  });
  const [deletePhase] = useDeletePhaseMutation();

  const handleFilterDrawerClose = (filterValues?: {
    status: CompanyStatus;
  }) => {
    onClose();
    if (!filterValues) return;

    setFilters({ status: filterValues.status });

    void refetch();
  };

  const handleRemoveFilter = (filterKey: keyof typeof filters) => {
    setFilters({ ...filters, [filterKey]: undefined });

    void refetch();
  };

  return (
    <Box h="full">
      {isOpen && (
        <FilterDrawer
          isOpen={isOpen}
          onClose={handleFilterDrawerClose}
          filters={phasesFilters}
          sorts={phasesSorts}
        />
      )}

      {isDeletePhaseModalOpen && (
        <DeletePhaseModal
          isOpen={isDeletePhaseModalOpen}
          onClose={isDeleted => {
            if (isDeleted && selectedPhase)
              void deletePhase({
                phaseId: selectedPhase,
                projectId: Number(projectId),
              });
            onCloseDeletePhaseModal();
          }}
        />
      )}

      <Box display="flex" flexDirection="column" flex="1" h="full">
        <HStack justify="space-between" mt="4" align="start">
          <Heading color="custom_blue.1" fontSize="2xl">
            Phases
          </Heading>

          <HStack>
            <Button
              onClick={() =>
                navigate(
                  `/projects/multiple-phase/${String(
                    projectId
                  )}/project-details/phases/new-phase`
                )
              }
              bg="custom_orange.2"
              _hover={{ bg: 'custom_orange.2' }}
              color="white"
              fontWeight="semibold"
              fontSize="sm"
            >
              <Image src={PlusIcon} alt="plus icon" boxSize="5" mr="1" />
              Add New Phase
            </Button>
          </HStack>
        </HStack>

        <VStack align="stretch" bg="white" my="4" gap="3">
          <HStack py="2">
            <InputGroup flexBasis="45%">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.300" />
              </InputLeftElement>
              <Input
                placeholder="Search"
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputGroup>

            <Button
              leftIcon={<FiltersIcon />}
              color="custom_blue.1"
              fontWeight="semibold"
              fontSize="sm"
              boxShadow="0 1px 2px 0 rgba(16, 24, 40, 0.05)"
              border="1px"
              borderColor="custom_blue.3"
              bg="none"
              _hover={{ bg: 'none' }}
              onClick={onOpen}
            >
              Filters
            </Button>
          </HStack>

          {filters.status && (
            <HStack>
              <RoundedBadge
                colorSchema={filters.status}
                withCloseButton
                onClose={handleRemoveFilter}
              >
                {filters.status}
              </RoundedBadge>
            </HStack>
          )}

          {isLoading ? (
            <CenteredSpinner />
          ) : (
            <Box>
              <TableContainer
                h="full"
                display="flex"
                flexDirection="column"
                justifyContent="space-between"
                minH="70vh"
              >
                <Table variant="simple">
                  <Thead bg="custom_gray.2">
                    <Tr>
                      <TableHeader title="Phase Name" />
                      <TableHeader title="Phase Start Date" />
                      <TableHeader title="Phase End Date" />
                      <TableHeader title="Members" />
                      {/* <TableHeader title="Progress" /> */}
                      <TableHeader title="Actions" />
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data?.data.content.map(
                      ({
                        id,
                        phaseName,
                        phaseStartDate,
                        members,
                        phaseEndDate,
                        progress,
                        phaseCode,
                      }) => (
                        <LinkBox key={id} as={Tr}>
                          <Td border="none" fontSize="sm" fontWeight="medium">
                            <LinkOverlay
                              as={Link}
                              to={`/projects/multiple-phase/${String(
                                projectId
                              )}/project-details/phases/${id}`}
                            >
                              {phaseName} ({phaseCode})
                            </LinkOverlay>
                          </Td>
                          <Td border="none" fontSize="md">
                            {formatDate(phaseStartDate)}
                          </Td>
                          <Td border="none" fontSize="md">
                            {formatDate(phaseEndDate)}
                          </Td>

                          <Td color="custom_blue.1" fontSize="sm" border="none">
                            {members}
                          </Td>

                          {/* <Td w="48" border="none">
                            <CustomProgress value={progress} />
                          </Td> */}
                          <Td border="none">
                            <HStack>
                              <IconButton
                                icon={<RedTrashIcon />}
                                aria-label="delete phase"
                                bg="transparent"
                                onClick={() => {
                                  onOpenDeletePhaseModal();
                                  setSelectedProject(id);
                                }}
                              />
                              <IconButton
                                icon={<EditIcon />}
                                aria-label="edit phase"
                                bg="transparent"
                                onClick={() =>
                                  navigate(
                                    `/projects/multiple-phase/${String(
                                      projectId
                                    )}/project-details/phases/update/${id}`
                                  )
                                }
                              />
                            </HStack>
                          </Td>
                        </LinkBox>
                      )
                    )}
                  </Tbody>
                </Table>

                <Paginator
                  pageCount={Number(data?.data.totalPages)}
                  onPageChange={page => setPage(page)}
                />
              </TableContainer>
            </Box>
          )}
        </VStack>
      </Box>
    </Box>
  );
};
